import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Chip,
  Paper,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { DATABASE } from "../../../../Config/firebase";
import {
  DEFAULT_CLIENT_DATE_REMINDERS,
  mergeClientDateReminderConfig,
} from "../../../../Utils/clientDateReminders";

const fieldSx = {
  mb: 2,
  "& .MuiInputBase-root": { color: "#fff" },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
};

/**
 * Persist client_date_reminders under admin_settings/config.
 */
export default function DateReminderSettingsTab({ onConfigChange }) {
  const [cfg, setCfg] = useState(() => mergeClientDateReminderConfig(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offsetDraft, setOffsetDraft] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(DATABASE, "admin_settings", "config"));
        const raw = snap.exists() ? snap.data()?.client_date_reminders : null;
        const merged = mergeClientDateReminderConfig(raw);
        if (!cancelled) {
          setCfg(merged);
          onConfigChange?.(merged);
        }
      } catch (e) {
        console.error(e);
        toast.error("Failed to load date reminder settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onConfigChange]);

  const updateType = (key, enabled) => {
    setCfg((prev) => ({
      ...prev,
      types: { ...prev.types, [key]: { ...prev.types[key], enabled } },
    }));
  };

  const updateEvent = (id, patch) => {
    setCfg((prev) => ({
      ...prev,
      institutional_events: (prev.institutional_events || []).map((ev) =>
        ev.id === id ? { ...ev, ...patch } : ev
      ),
    }));
  };

  const addOffset = () => {
    const n = Number(offsetDraft);
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      toast.warning("Offset must be a non-negative integer (days before)");
      return;
    }
    setCfg((prev) => ({
      ...prev,
      offsets_days: [...new Set([...(prev.offsets_days || []), n])].sort((a, b) => b - a),
    }));
    setOffsetDraft("");
  };

  const removeOffset = (n) => {
    setCfg((prev) => {
      const next = (prev.offsets_days || []).filter((x) => x !== n);
      return { ...prev, offsets_days: next.length ? next : [0] };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const toSave = {
        enabled: cfg.enabled !== false,
        recipient_email: (cfg.recipient_email || "admin@fidusfinance.com").trim(),
        timezone: "Europe/London",
        send_hour_local: Number(cfg.send_hour_local) || 8,
        offsets_days: cfg.offsets_days,
        lookahead_days: Number(cfg.lookahead_days) || 30,
        types: cfg.types,
        institutional_events: (cfg.institutional_events || []).map((ev) => ({
          id: ev.id,
          label: ev.label,
          month: ev.month ?? null,
          day: ev.day ?? null,
          variable: ev.variable || null,
          enabled: ev.enabled !== false,
          tip_en: ev.tip_en || "",
        })),
      };
      await setDoc(
        doc(DATABASE, "admin_settings", "config"),
        { client_date_reminders: toSave },
        { merge: true }
      );
      const merged = mergeClientDateReminderConfig(toSave);
      setCfg(merged);
      onConfigChange?.(merged);
      toast.success("Date reminder settings saved");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>Loading settings…</Typography>;
  }

  return (
    <Box>
      <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 13, mb: 2 }}>
        Admin-only digests (no automatic email to clients). Cron checks hourly (Europe/London) and
        sends at the configured hour. Defaults from seed when unset.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={cfg.enabled !== false}
            onChange={(e) => setCfg((p) => ({ ...p, enabled: e.target.checked }))}
            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#2af598" } }}
          />
        }
        label={<Typography sx={{ color: "#fff" }}>Reminders enabled</Typography>}
      />

      <TextField
        fullWidth
        size="small"
        label="Recipient email"
        value={cfg.recipient_email || ""}
        onChange={(e) => setCfg((p) => ({ ...p, recipient_email: e.target.value }))}
        sx={fieldSx}
      />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          size="small"
          type="number"
          label="Send hour (London)"
          value={cfg.send_hour_local}
          onChange={(e) => setCfg((p) => ({ ...p, send_hour_local: Number(e.target.value) }))}
          inputProps={{ min: 0, max: 23 }}
          sx={{ ...fieldSx, width: 180 }}
        />
        <TextField
          size="small"
          type="number"
          label="Upcoming lookahead (days)"
          value={cfg.lookahead_days}
          onChange={(e) => setCfg((p) => ({ ...p, lookahead_days: Number(e.target.value) }))}
          inputProps={{ min: 1, max: 365 }}
          sx={{ ...fieldSx, width: 220 }}
        />
      </Box>

      <Typography sx={{ color: "#2af598", fontSize: 12, fontWeight: 700, letterSpacing: 1, mb: 1, mt: 1 }}>
        OFFSETS (DAYS BEFORE)
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12, mb: 1 }}>
        0 = day of · 1 = one day before. Add/remove freely.
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
        {(cfg.offsets_days || DEFAULT_CLIENT_DATE_REMINDERS.offsets_days).map((n) => (
          <Chip
            key={n}
            label={n === 0 ? "Day of (0)" : `T-${n}`}
            onDelete={() => removeOffset(n)}
            deleteIcon={<DeleteIcon sx={{ color: "rgba(255,255,255,0.5) !important" }} />}
            sx={{ bgcolor: "rgba(42,245,152,0.12)", color: "#fff" }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          size="small"
          type="number"
          label="Add offset"
          value={offsetDraft}
          onChange={(e) => setOffsetDraft(e.target.value)}
          sx={{ ...fieldSx, width: 140, mb: 0 }}
        />
        <Button startIcon={<AddIcon />} onClick={addOffset} sx={{ color: "#7eb8ff", textTransform: "none" }}>
          Add
        </Button>
      </Box>

      <Typography sx={{ color: "#2af598", fontSize: 12, fontWeight: 700, letterSpacing: 1, mb: 1 }}>
        EVENT TYPES
      </Typography>
      {[
        ["birthday", "Birthdays"],
        ["account_anniversary", "Account anniversaries"],
        ["custom", "Custom client dates"],
        ["institutional", "Institutional / commemorative"],
      ].map(([key, label]) => (
        <FormControlLabel
          key={key}
          control={
            <Switch
              checked={cfg.types?.[key]?.enabled !== false}
              onChange={(e) => updateType(key, e.target.checked)}
              sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#2af598" } }}
            />
          }
          label={<Typography sx={{ color: "rgba(255,255,255,0.85)" }}>{label}</Typography>}
        />
      ))}

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 2 }} />

      <Typography sx={{ color: "#2af598", fontSize: 12, fontWeight: 700, letterSpacing: 1, mb: 1 }}>
        INSTITUTIONAL CALENDAR
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {(cfg.institutional_events || []).map((ev) => (
          <Paper
            key={ev.id}
            sx={{
              p: 1.5,
              bgcolor: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={ev.enabled !== false}
                    onChange={(e) => updateEvent(ev.id, { enabled: e.target.checked })}
                    sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#c4b5fd" } }}
                  />
                }
                label={<Typography sx={{ color: "#fff", fontWeight: 600 }}>{ev.label}</Typography>}
              />
              {!ev.variable ? (
                <>
                  <TextField
                    size="small"
                    type="number"
                    label="Month"
                    value={ev.month ?? ""}
                    onChange={(e) => updateEvent(ev.id, { month: Number(e.target.value) })}
                    inputProps={{ min: 1, max: 12 }}
                    sx={{ ...fieldSx, width: 90, mb: 0 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Day"
                    value={ev.day ?? ""}
                    onChange={(e) => updateEvent(ev.id, { day: Number(e.target.value) })}
                    inputProps={{ min: 1, max: 31 }}
                    sx={{ ...fieldSx, width: 90, mb: 0 }}
                  />
                </>
              ) : (
                <Chip size="small" label={ev.variable} sx={{ color: "rgba(255,255,255,0.7)" }} />
              )}
            </Box>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Tip (EN)"
              value={ev.tip_en || ""}
              onChange={(e) => updateEvent(ev.id, { tip_en: e.target.value })}
              sx={{ ...fieldSx, mt: 1, mb: 0 }}
            />
          </Paper>
        ))}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
          onClick={handleSave}
          sx={{ bgcolor: "#2af598", color: "#0a1a12", "&:hover": { bgcolor: "#3dffb0" } }}
        >
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </Box>
    </Box>
  );
}
